import { transient, autoinject, useView } from "aurelia-framework";
import { VesselDetailModel, VesselEnergySavingEquipmentModel, GhgRatingFactorClientModel, GetVesselTypesGHGRatingPeerGroupModel } from "../../../../../models/vessels/vessel.models";
import { GhgRatingSummary as GhgRatingSummaryStep } from "./ghg-rating-tabs/ghg-rating-summary/ghg-rating-summary";
import { GhgRatingFactorsStep } from "./ghg-rating-tabs/ghg-rating-factors/ghg-rating-factors";
import { GhgImprovementStep } from "./ghg-rating-tabs/ghg-improvement/ghg-improvement";
import { ManageVesselService } from "../../../../../services/vessels/manage-vessel.service";
import { GhgRatingModel } from "../../../../../models/ghgs/ghg.models";
import { GhgRatingPeerGroup } from "./ghg-rating-tabs/ghg-rating-peer-group/ghg-rating-peer-group";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { VesselEexi } from "./ghg-rating-tabs/vessel-eexi/vessel-eexi";

class VesselGhgRatingTab {
    title: string;
    vm: any;
    model?: any;
    isVisible: boolean;
}

@autoinject
@transient()
@useView('views/pages/modules/vessels/steps/vessel-ghg-rating.html')
export class VesselGhgRating {

    tabs: VesselGhgRatingTab[] = [];
    currentGhgTab: VesselGhgRatingTab;
    model: VesselDetailModel;
    ghgRating: GhgRatingModel;
    vesselEnergySavingEquipments: VesselEnergySavingEquipmentModel[];
    ghgRatingFactors: GhgRatingFactorClientModel[];
    vesselTypeEEDIGroup: string;
    isSactioned: boolean;
    isFlagFake: boolean;
    isDirectDiesel: boolean;
    isDieselElectric: boolean;
    isShuttleTanker: boolean;
    isSteamTurbine: boolean;
    vesselTypeEEDIGroupCode: string;
    vesselTypesGHGRatingPeerGroup: GetVesselTypesGHGRatingPeerGroupModel[];
    public latestShipIndex: string;

    requestAnEnvironmentalVerification: () => void

    setGhgRatingTabAsSanctioned() {
        this.isSactioned = true;
        this.isFlagFake = true;

        if (this.ghgRating) {
            this.ghgRating.hasGhgRating = false;
            this.ghgRating.isVerified = false;
            this.ghgRating.sizeScore = 0;
        }
    }

    constructor(private ghgRatingSummary: GhgRatingSummaryStep,
        public ghgRatingFactorsVM: GhgRatingFactorsStep,
        public ghgImprovement: GhgImprovementStep,
        private vesselService: ManageVesselService,
        private ghgRatingPeerGroup: GhgRatingPeerGroup,
        private vesselEexi: VesselEexi,
        private userClaimsService: UserClaimsService) { }

    activate(model: { vessel: VesselDetailModel, isSanctioned: boolean, isFlagFake: boolean, latestShipIndex: string, requestAnEnvironmentalVerification: () => any }) {
        this.model = model.vessel;
        this.isSactioned = model.isSanctioned;
        this.isFlagFake = model.isFlagFake;
        this.latestShipIndex = model.latestShipIndex;
        this.requestAnEnvironmentalVerification = model.requestAnEnvironmentalVerification;

        this.vesselService.getVesselTypesGHGRatingPeerGroup(this.model.vesselTypeEEDIGroupCode).then(response => {
            this.vesselTypesGHGRatingPeerGroup = response;
        });

        this.vesselService.getVesselGhgSummary(this.model.vesselId).then(response => {
            this.ghgRating = response.ghgRating;
            this.ghgRatingFactors = response.ghgRatingFactors;

            if (this.isSactioned || this.isFlagFake) {
                this.setGhgRatingTabAsSanctioned();
            }

            this.vesselEnergySavingEquipments = response.vesselEnergySavingEquipments;
            this.vesselTypeEEDIGroup = response.vesselTypeEEDIGroup;
            this.vesselTypeEEDIGroupCode = response.vesselTypeEEDIGroupCode;
            this.isDieselElectric = response.isDieselElectric;
            this.isDirectDiesel = response.isDirectDiesel;
            this.isShuttleTanker = response.isShuttleTanker;
            this.isSteamTurbine = response.isSteamTurbine;

            this.tabs = [
                {
                    title: "GHG Rating Summary",
                    vm: this.ghgRatingSummary,
                    isVisible: true,
                    model: {
                        ghgRating: this.ghgRating,
                        vesselEnergySavingEquipments: this.vesselEnergySavingEquipments,
                        vesselTypeEEDIGroup: this.vesselTypeEEDIGroup,
                        vesselTypesGHGRatingPeerGroup: this.vesselTypesGHGRatingPeerGroup,
                        energyPowerLimitationTypeId: this.model.energyPowerLimitationTypeId,
                        eplShaPoLiDateOfImplementation: this.model.eplShaPoLiDateOfImplementation,
                        latestShipIndex: this.latestShipIndex,
                    }
                },
                {
                    title: "GHG Rating Factors",
                    vm: this.ghgRatingFactorsVM,
                    isVisible: true,
                    model: {
                        ghgRating: this.ghgRating,
                        vesselEnergySavingEquipments: this.vesselEnergySavingEquipments,
                        vesselTypeEEDIGroup: this.vesselTypeEEDIGroup,
                        ghgRatingFactors: this.ghgRatingFactors,
                        vesselTypeEEDIGroupCode: this.vesselTypeEEDIGroupCode,
                        isDieselElectric: this.isDieselElectric,
                        isDirectDiesel: this.isDirectDiesel,
                        isShuttleTanker: this.isShuttleTanker,
                        isSteamTurbine: this.isSteamTurbine,
                        eplShaPoLiDetails: {
                            energyPowerLimitationTypeId: this.model.energyPowerLimitationTypeId,
                            eplShaPoLiDateOfImplementation: this.model.eplShaPoLiDateOfImplementation,
                            eplShaPoLiLastModifiedDate: this.model.eplShaPoLiLastModifiedDate
                        }
                    }
                },
                {
                    title: "EEXI",
                    vm: this.vesselEexi,
                    isVisible: this.userClaimsService.hasPermission(Claims.ShowGHG),
                    model: {
                        vesselId: this.model.vesselId, 
                        eedi: this.model.eedi, 
                        vesselTypeId: this.model.vesselTypeId, 
                        isSanctioned: this.isSactioned,
                        isFlagFake: this.isFlagFake
                    }
                },
                {
                    title: "GHG Rating peer group",
                    vm: this.ghgRatingPeerGroup,
                    isVisible: this.userClaimsService.hasPermission(Claims.CanAccessGHGRatingPeerGroup),
                    model: {
                        ghgRating: this.ghgRating,
                        vessel: this.model
                    }
                },
                {
                    title: "How can I improve?",
                    vm: this.ghgImprovement,
                    isVisible: true,
                    model: {
                        requestAnEnvironmentalVerification: this.requestAnEnvironmentalVerification
                    }
                }
            ];
            setTimeout(() => {
                this.navigateToTab(this.tabs[0]);
            });
        });
    }

    navigateToTab(tab: VesselGhgRatingTab) {
        this.currentGhgTab = tab;
        this.scrollToTop();
    }

    scrollToTop() {
        const scrollElement = MobileHelper.getScrollParent(document.getElementsByClassName('page-vessel').item(0), false) as HTMLDivElement;

        setTimeout(() => {
            if (scrollElement) {
                scrollElement.scrollTop = 0;
            }
        });
    }
}
